import { Component, computed, inject, input, signal, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterLink } from '@angular/router';
import { MobileScaleService } from '../../services/mobile-scale.service';
import { TeamListService, TeamSummary } from '../../services/team-list.service';
import { MAX_TEAMS } from '../../services/team.service';
import { OnchainStateService } from '../../services/onchain-state.service';
import { describeMon, MonDisplay } from '../../services/mon-display';
import { FacetEffect, facetEffect } from '../../data/facets';
import { MonStaticSpriteComponent } from '../mon-static-sprite/mon-static-sprite.component';
import { TeamSelectSheetComponent } from './team-select-sheet.component';

export type TeamSelectMode = 'home' | 'teams';

interface LineupMon extends MonDisplay {
  fx: FacetEffect | null;
}

@Component({
  selector: 'app-team-select',
  imports: [CommonModule, RouterLink, MonStaticSpriteComponent, TeamSelectSheetComponent],
  templateUrl: './team-select.component.html',
  styleUrls: ['../../styles/team-dropdown-kit.css', './team-select.component.css', './team-select.component.mobile.css'],
  host: {
    '(document:click)': 'onDocumentClick($event)',
    '(document:keydown.escape)': 'onEscape()',
  },
})
export class TeamSelectComponent {
  mode = input.required<TeamSelectMode>();
  // Home mode normally flies the dropdown left (into the right-edge sidebar).
  // Set true when the trigger sits in a left column so it drops down instead.
  readonly dropDown = input(false);
  /** Single-row variant for the mobile deck header: head + a 32px lineup strip, no card frame. */
  readonly compact = input(false);

  private elementRef = inject(ElementRef);
  private router = inject(Router);
  protected mobile = inject(MobileScaleService);
  protected teamList = inject(TeamListService);
  private onchain = inject(OnchainStateService);

  readonly isOpen = signal(false);
  readonly MAX_TEAMS = MAX_TEAMS;

  readonly active = computed<TeamSummary | null>(() => {
    const activeIdx = this.teamList.activeIndex();
    return this.teamList.teams().find(t => t.slot === activeIdx) ?? null;
  });

  // Header label follows a staged draft (teams mode) instead of the still-active
  // on-chain team, so creating a new team reads as the new team, not the old one.
  readonly headerName = computed<string>(() => {
    const draft = this.teamList.draftSlot();
    if (this.mode() === 'teams' && draft !== null) return this.teamList.nameForSlot(draft);
    return this.active()?.name ?? 'No team';
  });

  readonly headerIsDefault = computed<boolean>(() => {
    if (this.mode() === 'teams' && this.teamList.draftSlot() !== null) return false;
    return this.active()?.isDefault ?? false;
  });

  readonly isEmpty = computed(() => this.teamList.teams().length === 0);

  // Pre-resolved dropdown rows — each mon's name/level/facet memoized here
  // instead of via per-mon template method calls.
  readonly rows = computed(() =>
    this.teamList.teams().map(team => ({
      slot: team.slot,
      name: team.name,
      isDefault: team.isDefault,
      isActive: team.isActive,
      mons: team.monIds.map(id => describeMon(this.onchain, id)),
    }))
  );

  readonly lineup = computed<LineupMon[]>(() =>
    (this.active()?.monIds ?? []).map(id => {
      const m = describeMon(this.onchain, id);
      return { ...m, fx: m.facet ? facetEffect(m.facet) : null };
    })
  );

  toggleOpen(): void {
    if (this.isEmpty()) {
      this.router.navigate(['/teams']);
      return;
    }
    this.isOpen.update(v => !v);
  }

  closeDropdown(): void {
    this.isOpen.set(false);
  }

  selectRow(slot: number): void {
    this.teamList.selectTeam(slot);
    this.closeDropdown();
  }

  newTeam(): void {
    this.teamList.beginDraft();
    if (this.mode() === 'home') {
      this.router.navigate(['/teams']);
    }
    this.closeDropdown();
  }

  monIdAsNumber(id: bigint): number {
    return Number(id);
  }

  abbrevFacet(name: string): string {
    return name.slice(0, 3).toUpperCase();
  }

  onDocumentClick(event: MouseEvent): void {
    if (!this.isOpen()) return;
    const path = event.composedPath();
    if (!path.includes(this.elementRef.nativeElement)) {
      this.closeDropdown();
    }
  }

  onEscape(): void {
    this.closeDropdown();
  }
}
